import React from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

import { basicReducer as reducer } from '../../../../utils/reducers';
import { API } from 'aws-amplify';

import useStyles from './styles';

/**A Hook that takes two parameters and returns various state variables and handler functions necessary for the ChangeApplicationStatus modal.
 *
 * @param {*} AppInstID The Application Instance ID, used for invoking the API to change the status.
 * @param {*} userSettings The userSettings object, used for the ClientTimeZone for the approvedDate.
 * @returns {object} An object containing the state variables and functions necessary for the UI. Examples of StateVariables include isInitialized, approvedDate, inError, showValidation, etc.
 */
const useChangeApplicationStatus = (AppInstID, userSettings) => {
    const initialState = {
        newStatus: -1,
        approvedDate: moment.tz(new Date(), userSettings?.ClientTimeZone || 'America/New_York'),
        changeReason: '',
        suppressEmail: false,
        statusOptions: [],
        isInitialized: false,
        isDoingAjax: false,
        showValidation: false,
        maxScholarshipAmt: 0,
        adminFee: 0,
        netScholarshipAmtCalc: 0,
        inError: ['newStatus'],
    };
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const styles = useStyles();

    const translateCurrency = (val) => {
        return val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace('$', '$ ');
    };

    const initializeStatusChange = async (currentStatus, close = () => null) => {
        let initSuccess = false;
        let toastId = toast.loading('Initializing change status modal.');

        try {
            let { statuses } = await API.get('ShipyardAPI', `/statuschange/statuses/${currentStatus}`);

            toast.success('Statuses retrieved.');

            dispatch({
                type: 'update',
                newState: {
                    statusOptions: statuses,
                    isInitialized: true,
                },
            });

            initSuccess = true;
        } catch (e) {
            toast.error('API error.');
        } finally {
            setTimeout(() => toast.dismiss(toastId), 1000);
            if (initSuccess === false) {
                close?.();
            }
        }
    };

    const saveChanges = async () => {
        dispatch({
            type: 'update',
            newState: {
                isDoingAjax: true,
                showValidation: true,
            },
        });

        if (state?.inError?.length > 0) {
            dispatch({
                type: 'ajaxDone',
            });
            return false;
        }
        let toastId = toast.loading('Processing status change.');

        let successfulSave = false;
        try {
            await API.patch('ShipyardAPI', `/statuschange/${AppInstID}`, {
                body: {
                    newStatusId: state.newStatus,
                    changeReason: state.changeReason,
                    approvedDate: state?.newStatus === 6 ? state.approvedDate?.toISOString?.() : null,
                    suppressEmail: state?.suppressEmail,
                },
            });

            toast.success('Status change successful.');

            successfulSave = true;
        } catch (e) {
            toast.error('API error.');
        } finally {
            dispatch({
                type: 'ajaxDone',
            });

            toast.dismiss(toastId);

            return successfulSave;
        }
    };

    const handleDateChange = (newVal) => {
        var inError = new Set(state.inError);
        if (newVal === null) {
            inError.add('approvedDate');
        } else if (newVal !== null && state.approvedDate === null && inError.has('approvedDate')) {
            //Should only hit this when the field is currently in error, and is changing from blank to something not blank
            //handleDateError does not seem to be invoked in this specific scenario for some reason
            inError.delete('approvedDate');
        }

        dispatch({
            type: 'update',
            newState: {
                approvedDate: newVal,
                inError: Array.from(inError),
            },
        });
    };

    const handleDateError = (err, newVal) => {
        var inError = new Set(state.inError);
        if (err === null && newVal === null) {
            inError.add('approvedDate');
        } else if (err !== null) {
            inError.add('approvedDate');
        } else {
            inError.delete('approvedDate');
        }

        dispatch({
            type: 'update',
            newState: {
                approvedDate: newVal,
                inError: Array.from(inError),
            },
        });
    };

    const handleStatusChange = async (newVal) => {
        let _inError = new Set(state.inError);
        dispatch({
            type: 'update',
            newState: {
                newStatus: newVal || null,
                approvedDate: initialState.approvedDate,
                changeReason: initialState.changeReason,
                maxScholarshipAmt: initialState.maxScholarshipAmt,
                adminFee: initialState.adminFee,
                netScholarshipAmtCalc: initialState.netScholarshipAmtCalc,
                showValidation: initialState.showValidation,
            },
        });

        newVal > 0 ? _inError.delete('newStatus') : _inError.add('newStatus');

        //#region Pending Acceptance Logic
        if (newVal === 8) {
            let toastId = toast.loading('Calculating scholarship amounts.');

            dispatch({
                type: 'doingAjax',
            });

            try {
                let { scholarshipData } = await API.get('ShipyardAPI', `/applications/${AppInstID}/scholarship_data`, {
                    queryStringParameters: {
                        status: newVal,
                    },
                });

                toast.success('Scholarship amounts calculated.');

                dispatch({
                    type: 'update',
                    newState: {
                        maxScholarshipAmt: scholarshipData?.MaxScholarshipAmtCalc || 0,
                        adminFee: scholarshipData?.AdminFee || 0,
                        netScholarshipAmtCalc: scholarshipData?.NetScholarshipAmtCalc || 0,
                    },
                });
            } catch (e) {
                toast.error('API error.');
            } finally {
                dispatch({
                    type: 'ajaxDone',
                });
                toast.dismiss(toastId);
            }
        }
        //#endregion Pending Acceptance Logic

        //#region Change Reason required
        if ([3, 5].includes(newVal)) {
            _inError.add('changeReason');
        } else {
            _inError.delete('changeReason');
        }
        //#endregion Change Reason required

        dispatch({
            type: 'update',
            newState: {
                inError: Array.from(_inError),
            },
        });
    };

    const handleChangeReasonChange = (newVal) => {
        dispatch({
            type: 'update',
            newState: {
                changeReason: newVal || '',
                inError:
                    typeof newVal === 'string' && newVal !== ''
                        ? Array.from(new Set(state.inError).delete('changeReason'))
                        : Array.from(new Set(state.inError).add('changeReason')),
            },
        });
    };

    const handleSuppressEmailChange = (newVal) => {
        dispatch({
            type: 'update',
            newState: {
                suppressEmail: newVal || false,
            },
        });
    };

    const resetModal = () => {
        dispatch({
            type: 'update',
            newState: initialState,
        });
    };

    return {
        ...state,
        styles,
        initializeStatusChange,
        saveChanges,
        resetModal,
        handleDateChange,
        handleDateError,
        handleStatusChange,
        handleChangeReasonChange,
        handleSuppressEmailChange,
        translateCurrency,
    };
};

export default useChangeApplicationStatus;

